import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, FlaskConical, Loader2, XCircle } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import type { MetaCapiSettings } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useDeleteMetaCapiMutation,
  useMetaCapiQuery,
  useUpdateMetaCapiMutation,
} from "@/features/shops/api";
import { ApiError } from "@/lib/api-client";
import { useConfirm } from "@/lib/confirm";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

/** Biçimler backend `MetaCapiSettingsHandler` ile aynı. */
const schema = z.object({
  accessToken: z
    .string()
    .trim()
    .refine((v) => v === "" || /^[A-Za-z0-9_\-|.]{20,1000}$/.test(v), "validation.capiTokenInvalid"),
  testEventCode: z
    .string()
    .trim()
    .refine((v) => v === "" || /^[A-Za-z0-9_-]{3,40}$/.test(v), "validation.capiTestCodeInvalid"),
});

type CapiFormValues = z.infer<typeof schema>;

interface MetaCapiSectionProps {
  shopSlug: string;
  /** Kayıtlı Pixel kimliği; CAPI aynı pixel'e gönderir. */
  pixelId: string | null;
  disabled?: boolean;
}

export function MetaCapiSection({ shopSlug, pixelId, disabled }: MetaCapiSectionProps) {
  const t = useT("shops.tracking.capi");
  const query = useMetaCapiQuery(shopSlug, Boolean(pixelId));

  let body: ReactNode;
  if (!pixelId) {
    body = <p className="text-sm text-muted-foreground">{t("pixelRequired")}</p>;
  } else if (query.isLoading) {
    body = <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  } else if (!query.data) {
    body = <p className="text-sm text-destructive">{t("loadFailed")}</p>;
  } else if (!query.data.available) {
    body = <p className="text-sm text-muted-foreground">{t("unavailable")}</p>;
  } else {
    body = <MetaCapiForm shopSlug={shopSlug} settings={query.data} disabled={disabled} />;
  }

  return (
    <section className="space-y-3 border-t pt-6">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">{t("title")}</h3>
        <p className="text-xs text-muted-foreground">{t("description")}</p>
      </div>
      {body}
    </section>
  );
}

function MetaCapiForm({
  shopSlug,
  settings,
  disabled,
}: {
  shopSlug: string;
  settings: MetaCapiSettings;
  disabled?: boolean;
}) {
  const t = useT("shops.tracking.capi");
  const fmt = useFormat();
  const confirm = useConfirm();
  const update = useUpdateMetaCapiMutation(shopSlug);
  const remove = useDeleteMetaCapiMutation(shopSlug);
  const [replacing, setReplacing] = useState(false);
  const form = useForm<CapiFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { accessToken: "", testEventCode: settings.testEventCode ?? "" },
  });

  const showTokenInput = !settings.configured || replacing;

  async function onSubmit(values: CapiFormValues) {
    const accessToken = values.accessToken.trim();
    if (!settings.configured && !accessToken) {
      form.setError("accessToken", { message: "validation.capiTokenRequired" });
      return;
    }
    try {
      const result = await update.mutateAsync({
        accessToken: accessToken || null,
        testEventCode: values.testEventCode.trim() || null,
      });
      form.reset({ accessToken: "", testEventCode: result.testEventCode ?? "" });
      setReplacing(false);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("saveFailed"));
    }
  }

  async function onRemove() {
    const ok = await confirm({
      title: t("removeConfirmTitle"),
      description: t("removeConfirmDescription"),
      confirmLabel: t("remove"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync();
      form.reset({ accessToken: "", testEventCode: "" });
      setReplacing(false);
      toast.success(t("removed"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("removeFailed"));
    }
  }

  const last = settings.lastDelivery;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={settings.configured ? "success" : "outline"}>
              {settings.configured ? t("statusConfigured") : t("statusOff")}
            </Badge>
            {settings.configured && settings.testEventCode ? (
              <Badge variant="warning">
                <FlaskConical />
                {t("testModeActive")}
              </Badge>
            ) : null}
          </div>

          {showTokenInput ? (
            <FormField
              control={form.control}
              name="accessToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("tokenLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("tokenPlaceholder")}
                      dir="ltr"
                      autoComplete="off"
                      spellCheck={false}
                      className="max-w-sm font-mono"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t("tokenHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          <FormField
            control={form.control}
            name="testEventCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("testCodeLabel")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder="TEST12345"
                    dir="ltr"
                    autoComplete="off"
                    spellCheck={false}
                    className="max-w-xs font-mono"
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t("testCodeHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t("save")}
            </Button>
            {settings.configured ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.resetField("accessToken");
                    setReplacing((v) => !v);
                  }}
                >
                  {replacing ? t("cancelReplace") : t("replaceToken")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  disabled={remove.isPending}
                  onClick={onRemove}
                >
                  {t("remove")}
                </Button>
              </>
            ) : null}
          </div>

          {settings.configured ? (
            last ? (
              <div className="space-y-1 text-xs">
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  {last.status === "Sent" ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-destructive" />
                  )}
                  {t(last.status === "Sent" ? "lastSent" : "lastFailed", {
                    order: last.orderNumber,
                    date: fmt.dateTime(last.at),
                  })}
                </p>
                {last.status === "Failed" && last.error ? (
                  <p dir="ltr" className="break-words font-mono text-destructive">
                    {last.error}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t("noDelivery")}</p>
            )
          ) : null}
        </fieldset>
      </form>
    </Form>
  );
}
